// The grown-up side of the call pages: sign-in, calls left, the free preview and buying.
// Always in English (payments and accounts stay English on every language version).
import { PRODUCTS, type ProductId } from '../shared/products.ts';

export interface Me {
  user: { email: string | null; name: string | null; blocked: boolean; admin: boolean } | null;
  left?: { santa: number; elf: number };
  previewsLeft?: number;
  signin: boolean;
  payments: boolean;
  devLogin: boolean;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const here = () => location.pathname;

export async function me(): Promise<Me> {
  const r = await fetch('/api/me');
  return r.json();
}

/** After returning from Stripe: record the purchase right away and clean the address bar. */
export async function confirmCheckout() {
  const q = new URLSearchParams(location.search);
  const id = q.get('session_id');
  if (q.get('checkout') === 'success' && id) {
    await fetch('/api/checkout/confirm', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ session_id: id }) }).catch(() => {});
  }
  if (q.has('checkout') || q.has('signin')) history.replaceState(null, '', here());
  return q.get('checkout');
}

export function signInCard(m: Me): string {
  if (!m.signin && !m.devLogin) return `<div class="account-card"><p>Sign-in isn't set up yet on this server.</p></div>`;
  return `<div class="account-card">
    <h2>Sign in to begin</h2>
    <p>A grown-up signs in with Google to try a free preview or start a call. We use it to keep calls safe and fair; we never post anything or see your contacts.</p>
    ${m.signin ? `<a class="btn btn-google" href="/auth/google?return=${encodeURIComponent(here())}"><span class="g">G</span> Sign in with Google</a>` : ''}
    ${m.devLogin ? `<button class="btn btn-ghost" type="button" data-devlogin>Local test sign-in</button>` : ''}
    <p class="small">By signing in you agree to the <a href="/terms">terms</a> and <a href="/privacy">privacy policy</a>.</p>
  </div>`;
}

export function accountBar(m: Me): string {
  if (!m.user) return '';
  return `<div class="account-bar">
    <span>${esc(m.user.email || '')}</span>
    <span class="left">Santa calls: <b>${m.left?.santa ?? 0}</b> · Elf calls: <b>${m.left?.elf ?? 0}</b></span>
    ${m.user.admin ? '<a href="/admin">Admin</a>' : ''}
    <button type="button" class="linkish" data-logout>Sign out</button>
    <button type="button" class="linkish" data-delete>Delete account</button>
  </div>`;
}

export function offersCard(kind: 'santa' | 'elf', m: Me): string {
  const order: ProductId[] = kind === 'santa' ? ['santa', 'bundle', 'elf'] : ['elf', 'bundle', 'santa'];
  const who = kind === 'santa' ? 'Santa' : 'Pipa the elf';
  return `<div class="account-card offers">
    <h2>${kind === 'santa' ? 'Ready for the real call?' : 'Call the elves every day'}</h2>
    <p>Try a short free preview first: ${who} says hello to your child by name, in the animated room.</p>
    <form class="preview-form" data-preview>
      <input name="name" maxlength="40" placeholder="Child's first name" autocomplete="off" required>
      <button class="btn" type="submit" ${m.previewsLeft ? '' : 'disabled'}>Play the free preview</button>
    </form>
    ${m.previewsLeft ? '' : '<p class="small">You have used today\'s free previews. Come back tomorrow!</p>'}
    <div class="products">
      ${order
        .map((id) => {
          const p = PRODUCTS[id];
          return `<div class="product${id === order[0] ? ' main' : ''}"><h3>${esc(p.name)}</h3><p class="price">${p.price}</p><p>${esc(p.blurb)}</p>
          <button class="btn ${id === order[0] ? 'btn-call' : 'btn-ghost'}" type="button" data-buy="${id}" ${m.payments ? '' : 'disabled'}>${m.payments ? 'Buy' : 'Coming soon'}</button></div>`;
        })
        .join('')}
    </div>
    <p class="small">Secure checkout by Stripe. Prices include VAT. Calls never expire this Christmas.</p>
  </div>`;
}

/** Wire the buttons inside a container (sign in, sign out, buy). */
export function wireAccount(root: HTMLElement, refresh: () => void) {
  root.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-devlogin]')) {
      await fetch('/api/auth/dev', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email: 'henri.rujala@outlook.com' }) });
      refresh();
    } else if (t.closest('[data-logout]')) {
      await fetch('/api/auth/logout', { method: 'POST' });
      refresh();
    } else if (t.closest('[data-delete]')) {
      // Two steps instead of a pop-up: the first click asks, the second one deletes.
      const b = t.closest<HTMLButtonElement>('[data-delete]')!;
      if (!b.dataset.sure) {
        b.dataset.sure = '1';
        b.textContent = 'Really delete? Your calls left are lost. Click again to delete';
        setTimeout(() => (delete b.dataset.sure, (b.textContent = 'Delete account')), 6000);
        return;
      }
      await fetch('/api/account/delete', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ confirm: 'DELETE' }) });
      refresh();
    } else {
      const buy = t.closest<HTMLElement>('[data-buy]');
      if (!buy) return;
      (buy as HTMLButtonElement).disabled = true;
      const r = await fetch('/api/checkout', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ product: buy.dataset.buy, return: here() }) });
      const j = await r.json().catch(() => ({}));
      if (j.url) location.href = j.url;
      else {
        alertBox(root, j.error || 'Checkout is not available right now.');
        (buy as HTMLButtonElement).disabled = false;
      }
    }
  });
}

function alertBox(root: HTMLElement, text: string) {
  let box = root.querySelector<HTMLElement>('.account-error');
  if (!box) {
    box = document.createElement('p');
    box.className = 'error account-error';
    root.append(box);
  }
  box.textContent = text;
}
